import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { toast } from 'react-toastify';
import api from '../services/api';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentForm, setCommentForm] = useState({
    name: '',
    email: '',
    comment: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchPost();
    }
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await api.get(`/blog/posts/${slug}`);
      setPost(response.data);
      fetchComments(response.data.id);
    } catch (error) {
      console.error('Error fetching post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const response = await api.get(`/blog/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!commentForm.name || !commentForm.email || !commentForm.comment) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/blog/posts/${post.id}/comments`, commentForm);
      toast.success('Comment submitted for approval');
      setCommentForm({ name: '', email: '', comment: '' });
      fetchComments(post.id);
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error('Failed to submit comment');
    } finally {
      setSubmitting(false);
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    return `https://graduates.katinetti.ac.ke/${imageUrl}`;
  };

  // Social Sharing Functions
  const shareOnFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
  };

  const shareOnTwitter = () => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(post.title);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=600,height=400');
  };

  const shareOnWhatsApp = () => {
    const url = window.location.href;
    const text = encodeURIComponent(`Check out this article: ${post.title}\n\n${url}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
      setShowShareMenu(false);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white border-b-4 border-[#fbbf24] shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
              {post.category_name && (
                <Link to={`/blog?category=${post.category_slug}`} className="text-[#fbbf24] hover:underline">
                  {post.category_name}
                </Link>
              )}
              <span>📅 {new Date(post.published_at).toLocaleDateString()}</span>
              <span>👁️ {post.view_count || 0} views</span>
              <span>✏️ by {post.author_name || 'Admin'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back button */}
          <button onClick={() => navigate(-1)} className="text-primary hover:underline mb-4 flex items-center gap-1">
            ← Back
          </button>

          <Card className="overflow-hidden">
            {/* Featured Image */}
            {getImageUrl(post.featured_image) && (
              <div className="w-full h-96 overflow-hidden bg-gray-100">
                <img
                  src={getImageUrl(post.featured_image)}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            <div className="p-6 md:p-8">
              {/* Content */}
              <div 
                className="prose max-w-none text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>
          </Card>

          {/* Social Share Section */}
          <div className="mt-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <span className="text-gray-600 font-medium">Share this post:</span>
                <div className="flex gap-2">
                  <button
                    onClick={shareOnFacebook}
                    className="w-10 h-10 bg-[#1877f2] text-white rounded-full flex items-center justify-center hover:bg-[#166fe5] transition-colors"
                    title="Share on Facebook"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
                    </svg>
                  </button>
                  <button
                    onClick={shareOnTwitter}
                    className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                    title="Share on X (Twitter)"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </button>
                  <button
                    onClick={shareOnWhatsApp}
                    className="w-10 h-10 bg-[#25d366] text-white rounded-full flex items-center justify-center hover:bg-[#20bd5a] transition-colors"
                    title="Share on WhatsApp"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.03 20.75c-1.5 0-2.98-.4-4.27-1.17l-4.63 1.2 1.24-4.5c-.86-1.4-1.32-3.02-1.32-4.7 0-5.02 4.07-9.1 9.07-9.1 2.42 0 4.7.94 6.4 2.65 1.7 1.7 2.64 3.98 2.64 6.4 0 5-4.07 9.22-9.13 9.22zm0-16.7c-4.14 0-7.5 3.37-7.5 7.5 0 1.46.42 2.88 1.2 4.1l-.8 2.9 3-.8c1.2.7 2.6 1.1 4.1 1.1 4.14 0 7.5-3.37 7.5-7.5 0-4.14-3.36-7.3-7.5-7.3z"/>
                      <path d="M15.8 14.6c-.2-.3-.8-.8-1.4-1.1-.6-.3-1-.3-1.4.1-.3.4-.8 1.1-1 1.3-.2.3-.4.4-.8.2-.8-.3-1.6-1-2.2-1.8-.4-.5-.6-.9-.7-1.2 0-.3.1-.5.2-.7.1-.2.2-.3.3-.5.1-.2.2-.3.2-.5 0-.2-.1-.4-.2-.6l-.5-1c-.2-.4-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.2-.8.8-.8 2 0 1.2.9 2.4 1 2.6.1.2 1.7 2.6 4.1 3.6.6.2 1 .4 1.4.5.6.1 1.1.1 1.5 0 .5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.1-1.2z"/>
                    </svg>
                  </button>
                  <button
                    onClick={copyLink}
                    className="w-10 h-10 bg-gray-600 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors"
                    title="Copy Link"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Comments ({comments.length})</h2>
            
            {/* Comment Form */}
            <Card className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Leave a Comment</h3>
              <form onSubmit={handleCommentSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Your Name *"
                    value={commentForm.name}
                    onChange={(e) => setCommentForm({...commentForm, name: e.target.value})}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Your Email *"
                    value={commentForm.email}
                    onChange={(e) => setCommentForm({...commentForm, email: e.target.value})}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <textarea
                  rows="4"
                  placeholder="Your Comment *"
                  value={commentForm.comment}
                  onChange={(e) => setCommentForm({...commentForm, comment: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary mb-4"
                  required
                />
                <Button type="submit" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Comment'}
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  Your comment will be visible after approval by the admin.
                </p>
              </form>
            </Card>
            
            {/* Comments List */}
            {comments.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No comments yet. Be the first to comment!</p>
            ) : (
              <div className="space-y-4">
                {comments.map((comment) => (
                  <Card key={comment.id} className="bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-semibold text-gray-800">{comment.name}</span>
                        <span className="text-xs text-gray-400 ml-2">{new Date(comment.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <p className="text-gray-700">{comment.comment}</p>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
